// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Edits one guidance rule's markdown and risk classification.
 *
 * The parent owns persistence and cancellation state; this controlled editor
 * keeps the risk-score mapping consistent with read cards without coupling the
 * shared markdown editor to knowledge-base storage.
 */
import { Loader2, Save, X } from 'lucide-react'

import { Button } from '@/components/ui'
import { MarkdownEditor } from '@/components/ui/markdown-editor/markdown-editor'
import type { KnowledgeBaseItem } from '../types'
import { riskLevel, RISK_SCORE, type RiskLevel } from './guidanceContent'

interface GuidanceItemEditorProps {
  item: KnowledgeBaseItem
  content: string
  score: string
  saving: boolean
  onContentChange: (content: string) => void
  onScoreChange: (score: string) => void
  onCancel: () => void
  onSave: () => void
}

export function GuidanceItemEditor({
  item,
  content,
  score,
  saving,
  onContentChange,
  onScoreChange,
  onCancel,
  onSave,
}: GuidanceItemEditorProps) {
  const selectedRisk = riskLevel(Number(score))

  return (
    <li data-testid={`kb-row-${item.id}`} className="rounded-xl border border-slate-300 bg-[#fbfcfd] p-[18px] shadow-[0_6px_16px_rgba(15,23,42,.07)]">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <span className="rounded-lg bg-slate-100 px-2.5 py-1 font-mono text-xs font-semibold text-slate-600">{item.id}</span>
          <span className="text-[11px] font-semibold uppercase tracking-[.05em] text-slate-500">Editing</span>
        </div>
        <MarkdownEditor
          value={content}
          onChange={onContentChange}
          ariaLabel="Guidance markdown"
          toolbar="minimal"
        />
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[12.5px] font-medium text-slate-600">Risk level</span>
          <div className="flex gap-1 rounded-lg bg-slate-100 p-[3px]">
            {(['low', 'medium', 'high'] as RiskLevel[]).map(risk => (
              <button
                key={risk}
                type="button"
                onClick={() => onScoreChange(String(RISK_SCORE[risk]))}
                className={`h-7 rounded-md px-3.5 text-[12.5px] capitalize ${selectedRisk === risk
                  ? 'bg-white font-semibold text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,.14)]'
                  : 'font-medium text-slate-500 hover:text-slate-900'
                }`}
              >
                {risk}
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
              <X className="mr-1 h-4 w-4" /> Cancel
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving || !content.trim()} data-testid={`kb-item-save-${item.id}`}>
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save Changes
            </Button>
          </div>
        </div>
      </div>
    </li>
  )
}
